import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { SITE_INFO } from '../data/siteData'
import { useCMSSide } from '../hooks/useCMS'

export function HandelsbetingelserPage() {
  const { side: cmsSide } = useCMSSide('handelsbetingelser')

  useSEO({
    title: cmsSide?.seo_titel || 'Handelsbetingelser | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Handelsbetingelser for BoSteendahl.dk - Vendersgade 37, 7000 Fredericia, CVR 39751062. Betaling, afbud, brugsaftale og vilkår.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/logo-11-dec.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Betingelser &amp; Vilkår
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl font-bold text-[#1b3a4b] tracking-tight">
            Handelsbetingelser
          </h1>
          <p className="mt-3 text-base sm:text-lg text-[#64748B] max-w-xl mx-auto">
            Gældende for sessioner, forløb og online produkter hos BoSteendahl.dk
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm border border-[#E5E0D8] space-y-8 text-sm sm:text-base text-[#475569] leading-relaxed">
            {/* Virksomhedsoplysninger */}
            <div className="p-6 rounded-2xl bg-[#FAF8F5] border border-[#E5E0D8] space-y-2">
              <h2 className="font-serif text-lg font-bold text-[#1b3a4b]">
                Alle produkter og services udbydes af:
              </h2>
              <p className="text-sm font-semibold text-[#1b3a4b]">BoSteendahl.dk</p>
              <p className="text-sm">Vendersgade 37, 7000 Fredericia</p>
              <p className="text-sm">CVR-nr.: 39751062</p>
              <p className="text-sm">
                Mail:{' '}
                <a href={`mailto:${SITE_INFO.email}`} className="text-[#1b3a4b] underline font-medium whitespace-nowrap">
                  {SITE_INFO.email}
                </a>
              </p>
              <p className="text-sm">
                Telefon:{' '}
                <a href={`tel:${SITE_INFO.phoneRaw}`} className="text-[#1b3a4b] underline font-medium whitespace-nowrap">
                  {SITE_INFO.phone}
                </a>
              </p>
            </div>

            {/* Betingelser */}
            <div className="space-y-4">
              <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
                Betingelser
              </h3>
              <p>
                Betaling for fysiske og online sessioner skal ske direkte efter sessionens afslutning medmindre andet bliver aftalt.
              </p>
              <p>
                Sessioner kan betales med betalingskort på klinikken eller ved bankoverførsel til konto <strong>3409 1279 1593</strong>.
              </p>
              <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200 text-amber-950 font-medium">
                Betaling for forløb skal ske inden forløbets begyndelse medmindre andet er aftalt. Forløb sælges som en samlet pakke - det er ikke muligt at få refunderet betaling for forløb.
              </div>
              <p>
                Aftalte sessioner skal aflyses i henhold til gældende afbudspolitik inden aftalt tidspunkt ellers skal der betales fuld pris for sessionen. For sessioner i forløb gælder samme betingelser for afbud.
              </p>
            </div>

            {/* Online produkter */}
            <div className="space-y-4 pt-4 border-t border-[#E5E0D8]">
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Betingelser for online produkter udbudt af BoSteendahl.dk
              </h3>
              <p>
                Online produkter fra BoSteendahl.dk består af materiale, der gives adgang til via en privat medlemsside. Du vil straks efter køb modtage en e-mail med oplysninger om hvordan du får adgang til dit produkt.
              </p>
            </div>

            {/* Brugsaftale */}
            <div className="space-y-4 pt-4 border-t border-[#E5E0D8]">
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Brugsaftale
              </h3>
              <p>
                BoSteendahl.dk' materiale udleveret i fysisk form eller online-materiale er udelukkende henvendt til dig personligt og må kun benyttes af dig. Enhver form for videregivelse af materialet er ikke tilladt. Det er heller ikke tilladt for dig, eller tredje part, at omgå, eller forsøge at omgå, eventuelle sikkerhedsforanstaltninger i forbindelse med login, download, o.l. Som kunde er det dit ansvar at materialet ikke videregives af tredje part.
              </p>
              <p>
                Al online-materiale er omfattet af loven om ophavsret. Det er skrevet, layoutet, produceret og distribueret af BoSteendahl.dk.
              </p>
              <p>
                Online-materialet er udelukkende til dit personligt brug. Du må ikke – hverken gratis eller mod betaling – dele, kopiere, udlåne, publicere, sælge eller på nogen anden måde videregive BoSteendahl.dk' materiale.
              </p>
            </div>

            {/* Betaling */}
            <div className="space-y-4 pt-4 border-t border-[#E5E0D8]">
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Betaling
              </h3>
              <p>
                <strong>Online produkter:</strong> Alle priser er i danske kroner inkl. moms medmindre andet er angivet. Du kan betale med VisaDankort, VISA og Mastercard. Du modtager straks efter købet en faktura på email til den opgivne email-adresse. BoSteendahl.dk gemmer aldrig dine kortnumre, udløbsdato eller kontrolcifre.
              </p>
              <p>
                <strong>Betaling i klinikken:</strong> Alle priser er i danske kroner inkl. moms medmindre andet er angivet. Du kan betale med kontanter, som bankoverførsel, VisaDankort, VISA og Mastercard.
              </p>
            </div>

            {/* Fortrydelsesret */}
            <div className="space-y-4 pt-4 border-t border-[#E5E0D8]">
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Fortrydelsesret
              </h3>
              <p>
                På aftaler der er indgået i forbindelse med sessioner i klinikken er der ingen fortrydelsesret.
              </p>
              <p>
                På BoSteendahl.dk’ online produkter er der ingen returret, men du har som køber stadig to års reklamationsret ifølge købeloven. Ved køb af online produkter fraskriver du dig de normale 14 dages returret. Som forbruger har du normalt 14 dages fortrydelsesret, men denne fortrydelsesret bortfalder ved digitalt leverede varer og ydelser, så snart det købte tages i brug, da det derefter ikke kan tilbageleveres. Produktet betragtes som taget i brug, når du via e-mail er blevet tilsendt oplysninger om adgang til produktet.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="space-y-4 pt-4 border-t border-[#E5E0D8]">
              <h3 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Disclaimer
              </h3>
              <p>
                Al materiale fra BoSteendahl.dk, gratis såvel som tilkøbt, er baseret på egen uddannelse og erfaringer. Det skal betragtes som inspiration, der stilles til rådighed uden nogen form for garanti.
              </p>
              <p>
                BoSteendahl.dk vil gøre sit absolut bedste for at behandle, hjælpe og guide alle, der vælger at investere i sessioner, forløb, workshops og kurser hos BoSteendahl.dk, på en så god og givende måde som overhovedet muligt.
              </p>
              <p>
                Når det er sagt, så kan der selvfølgelig ikke garanteres nogle som helst form for resultater. Selvom du investerer i en ydelse hos BoSteendahl.dk, så vil det ikke være forbundet med nogen form for garanti eller mulighed for at stille BoSteendahl.dk til ansvar for manglende eller uønskede resultater.
              </p>
              <p>
                Arbejdet med egne udfordringer, sygdomme, udvikling, osv. er en proces, som vi som mennesker er i hele livet, og det kan tage mange former og retninger.
              </p>
              <p className="font-medium text-[#1b3a4b]">
                Det der tilbydes gennem BoSteendahl.dk er ikke et quick-fix, men en mulighed for, at du kan modtage behandling, støtte og værktøjer, der forhåbentlig kan hjælpe dig over tid, til at få det bedre.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
